<template>
  <ontos-dialog :model-value="true" :max-width="maxWidth" :persistent="persistent"
            @update:model-value="$emit('closed')">
    <v-card>
      <v-card-title>
        <ontos-page-header :title="title" :icon="icon" :icon-color="iconColor" :level="2"/>
      </v-card-title>
      <!-- Slot além da prop: há pergunta cujo corpo é uma lista, e não uma frase -->
      <v-card-text>
        <slot>{{ message }}</slot>
      </v-card-text>
      <!-- As ações vêm como DADO, e não como markup de quem usa: era o `v-col cols="auto"` com o
           `pl-2` a partir do segundo que cada diálogo repetia, e que fazia as linhas de botões
           divergirem de um para o outro. A linha em si saiu para o DialogActionRow, que é onde
           aquele espaçamento passou a morar. -->
      <ontos-dialog-action-row>
        <v-btn v-for="action of actions" :key="action.value"
               size="small" :color="action.color" :variant="action.text ? 'text' : undefined"
               @click="$emit('pick', action.value)">
          <v-icon v-if="action.icon" size="small" start>{{ action.icon }}</v-icon>
          {{ action.label }}
        </v-btn>
      </ontos-dialog-action-row>
    </v-card>
  </ontos-dialog>
</template>

<script lang="ts">
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import DialogActionRow from '@/components/shared/DialogActionRow.vue'
import {t} from '@/i18n'

/** Uma saída da pergunta. `value` é o que volta no `pick`. */
export type Ask = {
  value: string
  label: string
  icon?: string
  color?: string
  /** Botão discreto, para a saída que não é a resposta esperada — tipicamente o cancelar. */
  text?: boolean
}

/**
 * A saída única de um aviso, que não pergunta nada: a conta criada, a alteração recusada. Função, e não
 * constante, para o rótulo sair na língua da hora.
 */
export function okAction(): Ask {
  return {value: 'ok', label: t('ok'), icon: 'mdi-check', color: 'primary'}
}

/**
 * As saídas de "excluir isto?", como na exclusão do item (o padrão, segundo o usuário): Excluir em
 * `error`, que é a cor da ação sem volta, e Cancelar ao lado, botão como ele. Era do ambiente; a
 * propriedade passou a perguntar igual — antes era o `confirm` do navegador (relato do usuário) —, e a
 * segunda cópia virou esta.
 */
export function deleteActions(): Ask[] {
  return [
    {value: 'delete', label: t('delete'), icon: 'mdi-delete', color: 'error'},
    {value: 'cancel', label: t('cancel'), icon: 'mdi-cancel'}
  ]
}

/**
 * Uma pergunta de uma linha com as saídas em botões: título, corpo e as ações à direita.
 *
 * Existe porque esse arranjo estava escrito em cada diálogo que pergunta alguma coisa — o aviso
 * de alterações não salvas, a transferência de vínculo reverso, o impedimento de exclusão, e a
 * escolha entre mover e acrescentar no Kanban. Quatro cópias do mesmo `v-dialog > v-card` com a
 * mesma linha de botões, e já divergindo no espaçamento e no tamanho.
 *
 * **As ações entram como dado.** Em slot, quem usa teria de repetir o `v-col cols="auto"` e o
 * `pl-2` do segundo em diante — que é justamente a parte que divergia. Daqui sai só o `pick` com
 * o `value` da escolhida; o que cada uma significa é de quem perguntou.
 *
 * Fechar pelo ESC ou pelo fundo emite `closed`, e não um `pick`: desistir não é uma das saídas.
 */
@Component({emits: ['pick', 'closed'], name: 'ontos-ask-dialog',
  components: {'ontos-dialog-action-row': DialogActionRow}})
class AskDialog extends Vue {
  @Prop({type: String, required: true}) readonly title!: string
  /**
   * Um ícone antes do título, para o aviso que precisa se anunciar: a recusa, com o alerta vermelho
   * (pedido do usuário: no chassi das perguntas, não se via que era um erro).
   */
  @Prop({type: String, default: ''}) readonly icon!: string
  @Prop({type: String, default: ''}) readonly iconColor!: string
  /** O corpo, quando é uma frase. Formas mais ricas vão pelo slot padrão. */
  @Prop({type: String, default: ''}) readonly message!: string
  @Prop({type: Array, required: true}) readonly actions!: Ask[]
  @Prop({type: String, default: '480px'}) readonly maxWidth!: string
  /** Fecha só por uma das ações. Para quando sair sem escolher deixaria algo pela metade. */
  @Prop({type: Boolean, default: false}) readonly persistent!: boolean
}

export default toNative(AskDialog)
</script>
